// Canvas voice-command routing — the project layer behind the shared voice
// session (shared/functions/voiceSession.js → el.call('voiceProjectCommand',
// transcript) in command mode; architecture/VOICE_COMMANDS.md).
//
// Two stages:
//   1. parseVoiceCommand(text) — pure grammar (unit-tested in
//      voiceCommands.test.js) mapping utterances to canvas intents.
//   2. voiceProjectCommand(text) — executes an intent against the REGISTERED
//      canvas functions (applyViewport / fitToSelection / undo / addBoardPage
//      / toggle* …), guarding each target so surfaces without it (shell
//      routes off-canvas, pre-board-pages builds) fall through gracefully.
//      Unmatched speech goes to the freestyle build pipeline when the local
//      `smbls claude` bridge is connected; otherwise `{ handled: false }`
//      lets the caller hand it to the assistant thread.

const _canvasNorm = (text) =>
  (text || '')
    .toLowerCase()
    .replace(/[.,!?;:'"]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const _title = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

export const parseVoiceCommand = (text) => {
  const t = _canvasNorm(text)
  if (!t) return null

  // view — fit / reset / zoom
  if (
    /\b(fit (the )?(canvas|view|screen|board|selection|everything|all)|zoom to fit|fit to (view|screen))\b/.test(
      t
    )
  ) {
    return { intent: 'fit' }
  }
  if (
    /\b(reset (the )?(zoom|view|canvas)|zoom (to )?(100( percent| %)?|a hundred( percent)?)|actual size)\b/.test(
      t
    )
  ) {
    return { intent: 'resetView' }
  }
  {
    const m = t.match(/^zoom( in| out| closer| further| away| back)?$/)
    if (m) return { intent: 'zoom', dir: / out| further| away| back/.test(m[1] || '') ? -1 : 1 }
  }

  // history
  if (/^undo( that| last( change)?| it)?$/.test(t)) return { intent: 'undo' }
  if (/^redo( that| it)?$/.test(t)) return { intent: 'redo' }

  // board pages
  {
    const m = t.match(
      /^(?:create|add|make|start) (?:a |another |new )*page(?: (?:called|named|titled) (.+))?$/
    )
    if (m) return { intent: 'addPage', name: m[1] ? _title(m[1]) : '' }
  }
  {
    const m =
      t.match(/^(?:open|go to|switch to|show) (?:the )?page (.+)$/) ||
      t.match(/^(?:open|go to|switch to) (?:the )?(.+) page$/)
    if (m) return { intent: 'openPage', name: m[1] }
  }

  // panels + chrome
  if (/^(open|show|toggle|close|hide) (the )?design system( panel)?$/.test(t)) {
    return { intent: 'toggleDesignSystem' }
  }
  if (/^(open|show|toggle|close|hide) (the )?components?( panel)?$/.test(t)) {
    return { intent: 'toggleComponents' }
  }
  if (/^(open|show|toggle|close|hide) (the )?side ?bars?$/.test(t)) {
    return { intent: 'toggleSidebars' }
  }
  if (/^(open|show|toggle|close|hide) (the )?(canvas )?settings$/.test(t)) {
    return { intent: 'toggleSettings' }
  }
  if (/^(open|show|toggle|close|hide) (the )?(keyboard )?shortcuts$/.test(t)) {
    return { intent: 'toggleShortcuts' }
  }

  // theme — anchored so a build prompt merely MENTIONING "dark mode" never
  // hijacks ("make the hero background blue in dark mode" → freestyle).
  {
    const m = t.match(
      /^(?:(?:switch|change|set|turn|go|please|enable|use|make it)\s+)*(?:it\s+)?(?:on\s+|to\s+|into\s+)?(?:the\s+)?(dark|light)\s+(?:mode|theme)$/
    )
    if (m) return { intent: 'theme', value: m[1] }
  }
  if (/^toggle (the )?theme$/.test(t)) return { intent: 'theme', value: 'toggle' }

  // selection
  if (/^(clear|deselect)( the| all)?( selection)?$|^deselect everything$/.test(t)) {
    return { intent: 'clearSelection' }
  }
  if (/^delete (the )?(selection|selected( items?| tiles?)?)$/.test(t)) {
    return { intent: 'deleteSelection' }
  }
  if (/^duplicate (the )?(selection|selected( items?| tiles?)?|this)$/.test(t)) {
    return { intent: 'duplicateSelection' }
  }

  return null
}

export function voiceProjectCommand(text) {
  const fns = (this.context && this.context.functions) || {}
  const root = this.state && (this.state.root || this.state)
  const cmd = parseVoiceCommand(text)

  if (!cmd) {
    // Free-form speech — a build/edit instruction for the canvas agent when
    // the local bridge is live; otherwise let the shared layer hand it to
    // the assistant thread.
    if (fns.freestyleSend && root && root.freestyleStatus === 'connected') {
      this.call('freestyleSend', text)
      return { handled: true, awaiting: 'freestyle' }
    }
    return { handled: false }
  }

  const methods = (this.context && this.context.methods) || {}

  switch (cmd.intent) {
    case 'zoom': {
      // House v2-aware zoom steppers (canvas/methods/index.js) — never
      // reimplement the viewport math here.
      if (!methods.zoomIn || !methods.zoomOut) return { handled: false }
      this.call(cmd.dir > 0 ? 'zoomIn' : 'zoomOut')
      return {
        handled: true,
        label: this.call(
          'polyglot',
          cmd.dir > 0 ? 'canvas.voice.zoomedIn' : 'canvas.voice.zoomedOut'
        )
      }
    }
    case 'fit': {
      if (!fns.fitToSelection) return { handled: false }
      this.call('fitToSelection')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.fitToView') }
    }
    case 'resetView': {
      if (methods.zoomReset) this.call('zoomReset')
      else if (fns.resetCanvasView) this.call('resetCanvasView')
      else return { handled: false }
      return { handled: true, label: this.call('polyglot', 'canvas.voice.viewReset') }
    }
    case 'undo': {
      if (!fns.undo) return { handled: false }
      this.call('undo')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.undone') }
    }
    case 'redo': {
      if (!fns.redo) return { handled: false }
      this.call('redo')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.redone') }
    }
    case 'addPage': {
      if (!fns.addBoardPage) return { handled: false }
      this.call('addBoardPage', cmd.name || undefined)
      return {
        handled: true,
        label: cmd.name
          ? this.call('polyglot', 'canvas.voice.addedPageNamed').replace('{name}', cmd.name)
          : this.call('polyglot', 'canvas.voice.addedPage')
      }
    }
    case 'openPage': {
      if (!fns.getCanvasV2 || !fns.switchBoardPage) return { handled: false }
      const v2 = this.call('getCanvasV2')
      const pages = (v2 && v2.pages) || []
      const want = _canvasNorm(cmd.name)
      const page =
        pages.find((p) => _canvasNorm(p.name) === want) || pages.find((p) => _canvasNorm(p.name).includes(want))
      if (!page)
        return {
          handled: true,
          label: this.call('polyglot', 'canvas.voice.noPageCalled').replace('{name}', cmd.name)
        }
      this.call('switchBoardPage', page.id)
      return {
        handled: true,
        label: this.call('polyglot', 'canvas.voice.opened').replace('{name}', page.name)
      }
    }
    case 'toggleDesignSystem': {
      if (!fns.toggleCanvasDesignSystem) return { handled: false }
      this.call('toggleCanvasDesignSystem')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.designSystem') }
    }
    case 'toggleComponents': {
      if (!fns.toggleCanvasComponents) return { handled: false }
      this.call('toggleCanvasComponents')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.components') }
    }
    case 'toggleSidebars': {
      if (!fns.toggleSidebars) return { handled: false }
      this.call('toggleSidebars')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.sidebars') }
    }
    case 'toggleSettings': {
      if (!fns.toggleCanvasSettings) return { handled: false }
      this.call('toggleCanvasSettings')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.canvasSettings') }
    }
    case 'toggleShortcuts': {
      if (!fns.toggleShortcutsOverlay) return { handled: false }
      this.call('toggleShortcutsOverlay')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.shortcuts') }
    }
    case 'theme': {
      const current = (this.context && this.context.globalTheme) || 'dark'
      const value = cmd.value === 'toggle' ? (current === 'dark' ? 'light' : 'dark') : cmd.value
      if (value === current)
        return {
          handled: true,
          label: this.call('polyglot', 'canvas.voice.alreadyMode').replace(
            '{mode}',
            this.call(
              'polyglot',
              current === 'dark' ? 'canvas.voice.darkMode' : 'canvas.voice.lightMode'
            )
          )
        }
      // Canvas toggleTheme also syncs the board background; changeGlobalTheme
      // (shell) is the fallback off-canvas.
      if (methods.toggleTheme) this.call('toggleTheme')
      else if (fns.changeGlobalTheme) this.call('changeGlobalTheme', value)
      else return { handled: false }
      return {
        handled: true,
        label: this.call(
          'polyglot',
          value === 'dark' ? 'canvas.voice.darkMode' : 'canvas.voice.lightMode'
        )
      }
    }
    case 'clearSelection': {
      if (!fns.clearSelection) return { handled: false }
      this.call('clearSelection')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.selectionCleared') }
    }
    case 'deleteSelection': {
      if (!fns.deleteSelection) return { handled: false }
      this.call('deleteSelection')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.deleted') }
    }
    case 'duplicateSelection': {
      if (!fns.duplicateSelection) return { handled: false }
      this.call('duplicateSelection')
      return { handled: true, label: this.call('polyglot', 'canvas.voice.duplicated') }
    }
    default:
      return { handled: false }
  }
}
